import {
  ChargeType,
  PricingModel
} from "@/pages/settings/pages/products-v2/types";
import { periodInMonths, totalAfterDiscount } from "./consts";

// One bundle line as the bundle price rule reads it. contractsapi prices a
// bundle with the same rule, and both are tested against the same shared cases
// (fixtures/bundle_pricing_cases.json), so the drawer's live total and the
// saved one agree.
export interface BundlePricingLine {
  pricingModel: number;
  chargeType: ChargeType | null;
  price: number | null;
  quantity: number | null;
  // The term's duration; null counts as one month.
  period: number | null;
  // Set only when the line has its own dates; the end date is inclusive.
  startDate?: string | null;
  endDate?: string | null;
  // Absent means a percentage, as in the shared cases; null means an amount.
  isDiscountPercentage?: boolean | null;
  discountAmount?: number | null;
}

// A line's total over its whole period, after its own discount, as the Add
// service drawer prices it: flat rate is the price, flexible is price x
// quantity, anything else is price x quantity x months.
export const bundlePricingLineTotal = (line: BundlePricingLine): number => {
  const price = line.price ?? 0;
  const quantity = line.quantity ?? 0;
  const total =
    line.pricingModel === PricingModel.FLAT_RATE
      ? price
      : line.pricingModel === PricingModel.FLEXIBLE
        ? price * quantity
        : price *
          quantity *
          periodInMonths(
            line.chargeType === ChargeType.ONE_TIME,
            line.startDate,
            line.endDate,
            line.period
          );
  return totalAfterDiscount(
    total,
    line.discountAmount,
    line.isDiscountPercentage === undefined ? true : line.isDiscountPercentage
  );
};

// The bundle total: its line totals added left to right, unrounded, as the
// server adds them.
export const bundlePricingTotal = (lineTotals: number[]): number =>
  lineTotals.reduce((sum, total) => sum + total, 0);

// The bundle's own discount: a percentage, or an amount in the contract's
// currency; a null or zero amount is none.
export interface BundlePricingDiscount {
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
}

// The bundle total after its own discount, which comes last, by the line rule:
// a percentage of the total, or the amount in the total's own direction.
// Not clamped at zero, like the server's discounted bundle total.
export const bundlePricingDiscountedTotal = (
  total: number,
  { isDiscountPercentage, discountAmount }: BundlePricingDiscount
): number => totalAfterDiscount(total, discountAmount, isDiscountPercentage);
